import React from 'react';

interface OscillometryZScoreBarProps {
  label: string;
  value: number;
  reference: number;
  zScore: number;
  cv?: number;
  unit: string;
  scaleType?: 'linear' | 'log-r5' | 'log-ax'; // Different scale types
}

export const OscillometryZScoreBar: React.FC<OscillometryZScoreBarProps> = ({ label, value, reference, zScore, cv, unit, scaleType = 'linear' }) => {
  const clampedZScore = Math.max(-3, Math.min(3, zScore));

  const calculatePosition = (tickZ: number) => {
    if (scaleType === 'linear') {
      return ((tickZ + 3) / 6) * 100;
    } else if (scaleType === 'log-r5') {
      const normalizedZ = (tickZ + 3) / 6;
      const exponential = Math.pow(normalizedZ, 1.8);
      return exponential * 100;
    } else if (scaleType === 'log-ax') {
      const normalizedZ = (tickZ + 3) / 6;
      const exponential = Math.pow(normalizedZ, 2.5);
      return exponential * 100;
    }
    return 50;
  };

  const position = calculatePosition(clampedZScore);

  const generateGradient = () => {
    const greenEnd = calculatePosition(1);
    const yellowStart = calculatePosition(2);
    const redStart = calculatePosition(3);

    return `linear-gradient(to right, 
      #22c55e 0%, 
      #22c55e ${greenEnd}%, 
      #fbbf24 ${yellowStart}%, 
      #ef4444 ${redStart}%, 
      #ef4444 100%)`;
  };

  const generateTicks = () => {
    const ticks = [];
    for (let i = -3; i <= 3; i++) {
      const tickPos = calculatePosition(i);

      ticks.push(
        <div
          key={i}
          style={{
            position: 'absolute',
            left: `${tickPos}%`,
            transform: 'translateX(-50%)'
          }}
        >
          <div style={{
            width: '2px',
            height: '8px',
            backgroundColor: '#6b7280',
            margin: '0 auto'
          }} />
          {[-3, 0, 3].includes(i) && (
            <div style={{
              fontSize: '12px',
              color: '#6b7280',
              marginTop: '4px',
              whiteSpace: 'nowrap'
            }}>
              {i}
            </div>
          )}
        </div>
      );
    }
    return ticks;
  };

  return (
    <div style={{ marginBottom: '24px' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        marginBottom: '4px'
      }}>
        <div style={{ fontWeight: 600, fontSize: '20px' }}>{label}</div>
        <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{value.toFixed(2)}</div>
      </div>

      <div style={{
        fontSize: '14px',
        color: '#6b7280',
        marginBottom: '8px'
      }}>
        {unit} | Ref.: {reference.toFixed(2)}, Z: {zScore.toFixed(1)}
        {cv !== undefined && cv !== null && !isNaN(cv) ? `, CV: ${cv.toFixed(1)}%` : ', CV: n/a'}
      </div>

      <div style={{ position: 'relative' }}>
        <div style={{
          position: 'relative',
          height: '32px',
          marginBottom: '5px'
        }}>
          {/* Gradient background */}
          <div style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderRadius: '4px',
            background: generateGradient()
          }} />

          {/* Z-score marker - black diamond */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: `${position}%`,
            transform: 'translate(-50%, -50%) rotate(45deg)',
            width: '12px',
            height: '12px',
            backgroundColor: 'black',
            border: '1px solid white',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            zIndex: 20
          }} />
        </div>

        {/* Tick marks and labels below the bar */}
        <div style={{
          position: 'relative',
          height: '32px'
        }}>
          {generateTicks()}
        </div>
      </div>
    </div>
  );
};